/**
 * PatientProgression — Iter 73 advanced view (Multi-Clinic tier).
 *
 * Two stacked visualisations for cross-session insight:
 *
 *  1. Layered Body Chart — semi-transparent overlay of pain markers from
 *     every captured session, colour-coded oldest→newest so the clinician
 *     can SEE shrinkage / migration / worsening at a glance. Includes a
 *     "Capture today's chart" button that takes the current /interviews
 *     pain_points and snapshots them to /body-chart/snapshot.
 *
 *  2. Multi-Disciplinary Care Timeline — chronological merged feed of
 *     appointments + clinical notes + massage sessions + body-chart
 *     snapshots across every discipline the patient has seen, so a
 *     multi-disciplinary team can quickly orient themselves.
 *
 * Mounted at /admin/patients/:patient_id/progression.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Activity, Calendar, FileText, HeartHandshake, Camera, Loader2, ChevronLeft, BarChart3, Layers } from 'lucide-react';
import { toast } from 'sonner';

// Discipline → marker colour (tailwind hex). Used consistently across the page
// so a physio session reads the same on the body chart and the timeline.
const DISCIPLINE_HEX = {
  physiotherapy:   '#003D80',
  osteopathy:      '#6A1B9A',
  chiropractic:    '#1B5E20',
  massage_therapy: '#FFB000',
  fitness:         '#B71C1C',
};

export default function PatientProgression() {
  const { patient_id } = useParams();
  const [patient, setPatient] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [capturing, setCapturing] = useState(false);

  const reload = async () => {
    const [r1, r2] = await Promise.all([
      api.get(`/body-chart/timeline/${patient_id}`).catch(() => ({ data: [] })),
      api.get(`/care-timeline/${patient_id}`).catch(() => ({ data: [] })),
    ]);
    setSnapshots(r1.data || []);
    setTimeline(r2.data?.items || []);
  };

  useEffect(() => {
    (async () => {
      try {
        const [p] = await Promise.all([
          api.get(`/patients/${patient_id}`).catch(() => ({ data: null })),
        ]);
        setPatient(p.data);
        await reload();
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient_id]);

  const captureToday = async () => {
    setCapturing(true);
    try {
      // Pull the patient's current pain_points from /interviews to snapshot them.
      const intv = await api.get(`/interviews/${patient_id}`).catch(() => ({ data: { pain_points: [] } }));
      const points = intv.data?.pain_points || [];
      if (!points.length) {
        toast.error('No pain points to capture — patient hasn\'t marked their body chart yet.');
        return;
      }
      const overall = points.reduce((acc, p) => Math.max(acc, p.intensity || 0), 0);
      await api.post('/body-chart/snapshot', {
        patient_id,
        discipline: 'physiotherapy',
        overall_pain: overall,
        pain_points: points,
      });
      toast.success('Body chart snapshot captured.');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Capture failed.');
    } finally { setCapturing(false); }
  };

  if (loading) return <div className="p-8">Loading…</div>;

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <div className="mb-6">
        <Link to={`/admin/patients/${patient_id}`} className="text-xs text-fg-muted inline-flex items-center gap-1 hover:text-brand mb-2" data-testid="prog-back">
          <ChevronLeft className="w-3 h-3"/> Back to chart
        </Link>
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">PROGRESSION</div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight" data-testid="prog-title">
              Layered chart & care timeline
            </h1>
            <p className="text-fg-muted text-sm mt-1">
              {patient?.full_name && <>Patient: <strong>{patient.full_name}</strong> · </>}
              See how pain markers shift across sessions and what every discipline has been doing.
            </p>
          </div>
          <button onClick={captureToday} disabled={capturing}
                  className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="prog-capture">
            {capturing ? <Loader2 className="w-4 h-4 animate-spin"/> : <Camera className="w-4 h-4"/>}
            Capture today's chart
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Layered body chart */}
        <div className="bento-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-semibold flex items-center gap-2">
              <Layers className="w-4 h-4 text-brand"/> Layered body chart
            </h3>
            <span className="badge-neutral text-[10px]">{snapshots.length} session{snapshots.length === 1 ? '' : 's'}</span>
          </div>
          <LayeredBodyChart snapshots={snapshots}/>
          {snapshots.length === 0 && (
            <div className="text-center text-sm text-fg-muted mt-4" data-testid="prog-empty-chart">
              No snapshots yet. Tap <em>Capture today's chart</em> after the patient marks their pain points.
            </div>
          )}
        </div>

        {/* Pain trend chart (overall pain by session date) */}
        <div className="bento-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-semibold flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-brand"/> Overall pain trend
            </h3>
            <span className="badge-neutral text-[10px]">VAS 0-10</span>
          </div>
          <PainTrend snapshots={snapshots}/>
        </div>
      </div>

      {/* Multi-disciplinary care timeline */}
      <div className="bento-card mt-4">
        <h3 className="font-heading font-semibold mb-4 flex items-center gap-2">
          <Activity className="w-4 h-4 text-brand"/> Multi-disciplinary care timeline
        </h3>
        {timeline.length === 0 ? (
          <div className="text-center text-sm text-fg-muted py-8" data-testid="prog-timeline-empty">
            Nothing to show yet. Once appointments, notes or sessions are saved they appear here.
          </div>
        ) : (
          <div className="space-y-2" data-testid="prog-timeline">
            {timeline.map((item) => (
              <TimelineRow key={`${item.kind}-${item.id}`} item={item}/>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Layered Body Chart ----
function LayeredBodyChart({ snapshots }) {
  const sorted = useMemo(() => [...snapshots].sort((a, b) =>
    (a.session_date || '').localeCompare(b.session_date || '')), [snapshots]);

  // Group markers by view (anterior / posterior). For now we render only anterior
  // (matches the simplified 2D body chart from iter 67).
  const anteriorMarkers = useMemo(() => {
    const out = [];
    sorted.forEach((snap, idx) => {
      const opacity = 0.4 + (idx + 1) / Math.max(1, sorted.length) * 0.6; // fade old, vivid new
      (snap.pain_points || []).forEach((p) => {
        if ((p.view || 'anterior') !== 'anterior') return;
        out.push({
          x: p.x, y: p.y, intensity: p.intensity || 0,
          discipline: snap.discipline || 'physiotherapy',
          sessionLabel: (snap.session_date || '').slice(0, 10),
          sessionIdx: idx + 1,
          opacity,
        });
      });
    });
    return out;
  }, [sorted]);

  return (
    <div className="relative aspect-[1/2] max-w-[280px] mx-auto bg-[#FAF6EE] rounded-md border border-soft" data-testid="prog-bodychart">
      {/* Minimalist body silhouette using SVG. Keep it simple — the markers
          carry the meaning, not the body art. */}
      <svg viewBox="0 0 100 200" className="absolute inset-0 w-full h-full" aria-hidden="true">
        {/* Head */}
        <circle cx="50" cy="14" r="9" fill="#E5E2DA"/>
        {/* Torso */}
        <rect x="36" y="24" width="28" height="50" rx="6" fill="#E5E2DA"/>
        {/* Arms */}
        <rect x="22" y="26" width="10" height="42" rx="4" fill="#E5E2DA"/>
        <rect x="68" y="26" width="10" height="42" rx="4" fill="#E5E2DA"/>
        {/* Forearms */}
        <rect x="20" y="68" width="10" height="36" rx="4" fill="#E5E2DA"/>
        <rect x="70" y="68" width="10" height="36" rx="4" fill="#E5E2DA"/>
        {/* Pelvis */}
        <rect x="36" y="74" width="28" height="22" rx="6" fill="#E5E2DA"/>
        {/* Thighs */}
        <rect x="38" y="96" width="11" height="50" rx="4" fill="#E5E2DA"/>
        <rect x="51" y="96" width="11" height="50" rx="4" fill="#E5E2DA"/>
        {/* Calves */}
        <rect x="38" y="146" width="11" height="46" rx="4" fill="#E5E2DA"/>
        <rect x="51" y="146" width="11" height="46" rx="4" fill="#E5E2DA"/>
      </svg>
      {anteriorMarkers.map((m, i) => {
        const r = 6 + Math.min(m.intensity, 10) * 0.5;
        const colour = DISCIPLINE_HEX[m.discipline] || '#003D80';
        return (
          <span key={i}
                className="absolute rounded-full border-2 border-white shadow-sm pointer-events-auto"
                title={`Session ${m.sessionIdx} (${m.sessionLabel}) — pain ${m.intensity}/10`}
                style={{
                  left: `calc(${m.x * 100}% - ${r}px)`,
                  top:  `calc(${m.y * 100}% - ${r}px)`,
                  width: r * 2, height: r * 2,
                  background: colour,
                  opacity: m.opacity,
                }}
                data-testid={`prog-marker-${i}`}/>
        );
      })}
      {/* Legend */}
      <div className="absolute -right-2 top-0 translate-x-full hidden md:flex flex-col gap-1 text-[10px]">
        {Object.entries(DISCIPLINE_HEX).map(([d, hex]) => (
          <div key={d} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: hex }}/>
            <span className="capitalize text-fg-muted">{d.replace('_', ' ')}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Pain trend mini-chart ----
function PainTrend({ snapshots }) {
  const data = useMemo(() => [...snapshots]
    .sort((a, b) => (a.session_date || '').localeCompare(b.session_date || ''))
    .map((s) => ({
      date: (s.session_date || '').slice(0, 10),
      pain: s.overall_pain ?? 0,
      discipline: s.discipline || 'physiotherapy',
    })), [snapshots]);

  if (data.length === 0) {
    return <div className="text-center text-sm text-fg-muted py-8" data-testid="prog-trend-empty">No data yet.</div>;
  }
  const maxX = Math.max(1, data.length - 1);
  const path = data.map((d, i) => {
    const x = (i / maxX) * 100;
    const y = 100 - (d.pain / 10) * 100;
    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');

  return (
    <div className="relative" data-testid="prog-trend">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full aspect-[5/2] bg-[#FAF6EE] rounded-md">
        {/* Y-axis grid */}
        {[0, 25, 50, 75, 100].map((y) => (
          <line key={y} x1="0" x2="100" y1={y} y2={y} stroke="#E5E2DA" strokeWidth="0.3"/>
        ))}
        <path d={path} stroke="#003D80" strokeWidth="1.5" fill="none" vectorEffect="non-scaling-stroke"/>
        {data.map((d, i) => {
          const x = (i / maxX) * 100;
          const y = 100 - (d.pain / 10) * 100;
          return (
            <circle key={i} cx={x} cy={y} r="1.5"
                    fill={DISCIPLINE_HEX[d.discipline] || '#003D80'}
                    stroke="white" strokeWidth="0.3"
                    vectorEffect="non-scaling-stroke">
              <title>{`${d.date} — pain ${d.pain}/10`}</title>
            </circle>
          );
        })}
      </svg>
      <div className="flex justify-between text-[10px] text-fg-muted mt-1 px-1">
        <span>{data[0].date}</span>
        {data.length > 1 && <span>{data[data.length - 1].date}</span>}
      </div>
    </div>
  );
}

// ---- Timeline row ----
function TimelineRow({ item }) {
  const Icon = {
    appointment: Calendar, note: FileText, massage_session: HeartHandshake, body_chart: Layers,
  }[item.kind] || Activity;
  const colour = DISCIPLINE_HEX[item.discipline] || '#003D80';
  return (
    <div className="flex items-start gap-3 p-3 rounded-md border border-soft hover:bg-bg-secondary/30 transition" data-testid={`prog-tl-${item.kind}-${item.id}`}>
      <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
           style={{ background: `${colour}1A`, color: colour }}>
        <Icon className="w-3.5 h-3.5"/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{item.title}</div>
        <div className="text-[11px] text-fg-muted">{item.subtitle}</div>
      </div>
      <div className="text-[11px] text-fg-muted whitespace-nowrap text-right">
        <div>{(item.date || '').slice(0, 10)}</div>
        <span className="capitalize">{(item.discipline || '').replace('_', ' ')}</span>
      </div>
    </div>
  );
}
